// DefectsSection.js
// ─────────────────────────────────────────────────────────────────────────────
// DEFECTS & SNAG LIST — pre-completion snags and Defects Liability Period
// defects, tracked by chainage against the same progressAssets the Visual
// Progress Tracker uses.
//
// What it does
// ------------
//  • Register:  projects/{projectId}/defects — auto-numbered DEF-001, DEF-002…
//  • Each defect: category, severity, phase (Snag / DLP), asset + chainage
//    position, responsible party, target rectification date, photos.
//  • Close-out workflow:  Open → Notified → Rectified → Verified & Closed
//    (with Re-opened for failed verification). One-click status advance.
//  • Chainage defect map: compact SVG strip per asset showing where open and
//    closed defects sit along the route — same visual language as the tracker.
//  • Photos: captured via file input, compressed client-side (canvas →
//    max 1024px JPEG) and stored as data URLs on the doc. No Firebase Storage
//    dependency. Practical cap of 4 photos per defect to respect the 1 MB
//    Firestore document limit. Viewed through the existing PhotoLightbox.
//  • Print: A4 snag list grouped by asset, with thumbnails — for joint
//    inspections and DLP close-out.
//
// Conventions: tokens from theme.js, createPortal for modals, audit logging on
// create/delete, React.memo export, min-width: 0 on grid children, JetBrains
// Mono for refs/chainage/numbers.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";
import PhotoLightbox from "./PhotoLightbox";

const C = tokens.color;
const MONO = '"JetBrains Mono", monospace';

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORIES = [
  "Pipework", "Concrete", "Earthworks", "Reinstatement",
  "Valves & Fittings", "Coating / Lining", "Electrical / Mechanical", "Other",
];

const SEVERITIES = ["Minor", "Major", "Critical"];

const PHASES = ["Snag (Pre-Completion)", "DLP Defect"];

// Close-out workflow, in order. advance() steps forward; Re-opened loops back.
const STATUS_FLOW = ["Open", "Notified", "Rectified", "Verified & Closed"];

const STATUS_META = {
  "Open":              { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder },
  "Notified":          { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  "Rectified":         { bg: C.infoSoft,    text: C.info,    border: C.infoBorder },
  "Verified & Closed": { bg: C.successSoft, text: C.success, border: C.successBorder },
  "Re-opened":         { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder },
};

const SEVERITY_META = {
  Minor:    { bg: C.neutralSoft, text: C.neutral, border: C.neutralBorder },
  Major:    { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  Critical: { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder },
};

const MAX_PHOTOS = 4;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numberOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// Chainage display — same convention as the Visual Progress Tracker (k+mmm).
const fmtCh = (v) => {
  const n = numberOrZero(v);
  const km = Math.floor(n / 1000);
  const m = Math.round(n - km * 1000);
  return `${km}+${String(m).padStart(3, "0")}`;
};

function nextDefRef(items) {
  const nums = items
    .map((m) => parseInt((m.ref || "").replace("DEF-", ""), 10))
    .filter((n) => Number.isFinite(n));
  return `DEF-${String(nums.length ? Math.max(...nums) + 1 : 1).padStart(3, "0")}`;
}

const isClosed = (d) => d.status === "Verified & Closed";
const isOverdue = (d) => !isClosed(d) && d.targetDate && d.targetDate < todayISO();

// Client-side photo compression: resize longest edge to 1024px, JPEG q0.72.
// Keeps a handful of photos comfortably inside the 1 MB Firestore doc limit.
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        const maxEdge = 1024;
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.72));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 10px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13, color: C.ink, fontFamily: "inherit", outline: "none" },
  ta:  { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13, color: C.ink, fontFamily: "inherit", outline: "none", resize: "vertical", lineHeight: 1.5 },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnPrimary: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.primary, color: C.inkOnDark, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: "nowrap" }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 16, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
};

const emptyForm = {
  title: "",
  description: "",
  category: "Pipework",
  severity: "Minor",
  phase: "Snag (Pre-Completion)",
  progressAssetId: "",
  chainageStart: "",
  chainageEnd: "",
  structureRef: "",
  responsible: "Contractor",
  raisedDate: "",
  targetDate: "",
  notificationRef: "",
  remarks: "",
  photos: [],
};

// ─── Chainage defect map ─────────────────────────────────────────────────────
// One horizontal strip per asset: route bar with defect markers positioned by
// chainage. Open = brick red, in-progress = amber, closed = green.

function DefectMap({ assets, defects, onSelect }) {
  const byAsset = useMemo(() => {
    const map = {};
    defects.forEach((d) => {
      if (!d.progressAssetId) return;
      (map[d.progressAssetId] = map[d.progressAssetId] || []).push(d);
    });
    return map;
  }, [defects]);

  const rows = assets.filter((a) => (byAsset[a.id] || []).length > 0);
  if (!rows.length) return null;

  const markerColor = (d) =>
    isClosed(d) ? C.success :
    d.status === "Rectified" ? C.info :
    d.status === "Notified" ? C.warning : C.danger;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map((asset) => {
        const start = numberOrZero(asset.startChainage);
        const end = numberOrZero(asset.endChainage);
        const span = Math.max(end - start, 1);
        const list = byAsset[asset.id] || [];
        return (
          <div key={asset.id} style={{ minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
              <span style={{ fontWeight: 700 }}>{asset.name || asset.id}</span>
              <span style={{ ...s.mono, color: C.inkMuted }}>{fmtCh(start)} → {fmtCh(end)} · {list.length} defect{list.length === 1 ? "" : "s"}</span>
            </div>
            <svg viewBox="0 0 1000 34" style={{ width: "100%", height: 34, display: "block" }}>
              <rect x="0" y="13" width="1000" height="8" rx="4" fill={C.surfaceSunken} stroke={C.border} />
              {list.map((d) => {
                const cs = numberOrZero(d.chainageStart);
                const ce = d.chainageEnd === "" || d.chainageEnd === undefined ? cs : numberOrZero(d.chainageEnd);
                const x1 = Math.min(Math.max(((cs - start) / span) * 1000, 0), 1000);
                const x2 = Math.min(Math.max(((ce - start) / span) * 1000, 0), 1000);
                const col = markerColor(d);
                return ce > cs ? (
                  <rect key={d.id} x={x1} y={11} width={Math.max(x2 - x1, 3)} height={12} rx={2}
                        fill={col} opacity={0.85} style={{ cursor: "pointer" }}
                        onClick={() => onSelect(d)}>
                    <title>{d.ref} — {d.title} ({d.status}) {fmtCh(cs)}–{fmtCh(ce)}</title>
                  </rect>
                ) : (
                  <circle key={d.id} cx={x1} cy={17} r={6}
                          fill={col} stroke="#fff" strokeWidth={1.5} style={{ cursor: "pointer" }}
                          onClick={() => onSelect(d)}>
                    <title>{d.ref} — {d.title} ({d.status}) at {fmtCh(cs)}</title>
                  </circle>
                );
              })}
            </svg>
          </div>
        );
      })}
      <p style={{ ...s.sub, margin: 0 }}>
        <span style={{ color: C.danger, fontWeight: 700 }}>●</span> Open / Re-opened&nbsp;&nbsp;
        <span style={{ color: C.warning, fontWeight: 700 }}>●</span> Notified&nbsp;&nbsp;
        <span style={{ color: C.info, fontWeight: 700 }}>●</span> Rectified&nbsp;&nbsp;
        <span style={{ color: C.success, fontWeight: 700 }}>●</span> Verified & Closed — click a marker to open the defect.
      </p>
    </div>
  );
}

// ─── Defect editor modal ─────────────────────────────────────────────────────

function DefectModal({ initial, assets, onSave, onClose, busy }) {
  const [form, setForm] = useState(() => ({ ...emptyForm, raisedDate: todayISO(), ...initial }));
  const [photoError, setPhotoError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const addPhotos = async (files) => {
    setPhotoError("");
    const slots = MAX_PHOTOS - (form.photos || []).length;
    if (slots <= 0) { setPhotoError(`Maximum ${MAX_PHOTOS} photos per defect.`); return; }
    try {
      const picked = Array.from(files).slice(0, slots);
      const compressed = await Promise.all(picked.map(compressImage));
      const next = [...(form.photos || []), ...compressed.map((url) => ({ url, caption: "", takenAt: todayISO() }))];
      // Guard the Firestore 1 MB doc limit — rough size check on the data URLs.
      const approxBytes = next.reduce((n, p) => n + (p.url?.length || 0), 0);
      if (approxBytes > 850000) {
        setPhotoError("Photos exceed the document size budget — remove one or use fewer/smaller images.");
        return;
      }
      set({ photos: next });
    } catch (e) {
      setPhotoError(e.message || "Photo processing failed");
    }
  };

  const removePhoto = (idx) => set({ photos: (form.photos || []).filter((_, i) => i !== idx) });

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "32px 16px" }}>
      <div style={{ background: C.surface, borderRadius: 14, width: "min(760px, 100%)", padding: 22, boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ ...s.h3, margin: 0 }}>{initial?.id ? `Edit ${initial.ref}` : "New Defect / Snag"}</h3>
          <button style={s.btn} onClick={onClose}>✕</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 12, minWidth: 0 }}>
          <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <label style={s.lbl}>Title</label>
            <input style={s.inp} value={form.title} placeholder="e.g. Honeycombing on chamber wall, CH 3+420"
                   onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Category</label>
            <select style={s.inp} value={form.category} onChange={(e) => set({ category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Severity</label>
            <select style={s.inp} value={form.severity} onChange={(e) => set({ severity: e.target.value })}>
              {SEVERITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Phase</label>
            <select style={s.inp} value={form.phase} onChange={(e) => set({ phase: e.target.value })}>
              {PHASES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Responsible</label>
            <input style={s.inp} value={form.responsible} onChange={(e) => set({ responsible: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Asset</label>
            <select style={s.inp} value={form.progressAssetId} onChange={(e) => set({ progressAssetId: e.target.value })}>
              <option value="">— none / structure —</option>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Chainage Start (m)</label>
            <input type="number" style={{ ...s.inp, ...s.mono }} value={form.chainageStart}
                   placeholder="e.g. 3420"
                   onChange={(e) => set({ chainageStart: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Chainage End (m, optional)</label>
            <input type="number" style={{ ...s.inp, ...s.mono }} value={form.chainageEnd}
                   placeholder="blank = point defect"
                   onChange={(e) => set({ chainageEnd: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Structure Ref (optional)</label>
            <input style={s.inp} value={form.structureRef} placeholder="e.g. Valve chamber VC-07" onChange={(e) => set({ structureRef: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Raised</label>
            <input type="date" style={s.inp} value={form.raisedDate} onChange={(e) => set({ raisedDate: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Target Rectification</label>
            <input type="date" style={s.inp} value={form.targetDate} onChange={(e) => set({ targetDate: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={s.lbl}>Notification Ref</label>
            <input style={{ ...s.inp, ...s.mono }} value={form.notificationRef} placeholder="letter / instruction no." onChange={(e) => set({ notificationRef: e.target.value })} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={s.lbl}>Description</label>
          <textarea rows={3} style={s.ta} value={form.description}
                    placeholder="Defect detail, extent, and required rectification…"
                    onChange={(e) => set({ description: e.target.value })} />
        </div>

        {/* Photos */}
        <div style={{ marginBottom: 16 }}>
          <label style={s.lbl}>Photos ({(form.photos || []).length}/{MAX_PHOTOS})</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {(form.photos || []).map((p, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={p.url} alt={`Defect ${i + 1}`} style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}` }} />
                <button onClick={() => removePhoto(i)}
                        style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: 999, border: "none", background: C.danger, color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer", lineHeight: 1 }}>✕</button>
              </div>
            ))}
            {(form.photos || []).length < MAX_PHOTOS && (
              <label style={{ ...s.btn, display: "inline-flex", alignItems: "center", gap: 6 }}>
                📷 Add Photos
                <input type="file" accept="image/*" multiple style={{ display: "none" }}
                       onChange={(e) => { if (e.target.files?.length) addPhotos(e.target.files); e.target.value = ""; }} />
              </label>
            )}
          </div>
          {photoError && <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 600, color: C.danger }}>{photoError}</p>}
          <p style={{ ...s.sub, marginTop: 4 }}>Compressed to 1024px JPEG on-device before saving.</p>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button style={s.btn} onClick={onClose}>Cancel</button>
          <button style={s.btnPrimary} disabled={busy || !form.title.trim()} onClick={() => onSave(form)}>
            {busy ? "Saving…" : initial?.id ? "Save Changes" : "Add Defect"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Print modal — snag list grouped by asset ────────────────────────────────

function PrintSnagListModal({ defects, assets, currentProject, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const assetName = (id) => assets.find((a) => a.id === id)?.name || "General / Structures";
  const groups = useMemo(() => {
    const map = {};
    defects.forEach((d) => {
      const key = d.progressAssetId || "_general";
      (map[key] = map[key] || []).push(d);
    });
    return Object.entries(map);
  }, [defects]);

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "32px 16px" }}>
      <div className="def-print-sheet" style={{ background: "#fff", width: "min(860px, 100%)", borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", padding: "36px 42px", color: "#0E1116", fontFamily: '"Inter", sans-serif' }}>
        <div className="def-print-hide" style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 16 }}>
          <button style={s.btnPrimary} onClick={() => window.print()}>🖨 Print / Save PDF</button>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>

        <div style={{ borderBottom: `3px solid ${C.primary}`, paddingBottom: 12, marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary }}>Defects & Snag List</p>
          <h1 style={{ margin: "4px 0 2px", fontSize: 20, letterSpacing: "-0.02em" }}>
            {currentProject?.contractNumber} — {currentProject?.contractDescription}
          </h1>
          <p style={{ margin: 0, fontSize: 12, color: "#444" }}>
            Issued {fmtDate(todayISO())} · {defects.length} item{defects.length === 1 ? "" : "s"} listed
          </p>
        </div>

        {groups.map(([assetId, list]) => (
          <div key={assetId} style={{ marginBottom: 20, breakInside: "avoid" }}>
            <h2 style={{ fontSize: 13, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1.5px solid #0E1116", paddingBottom: 3 }}>
              {assetName(assetId === "_general" ? "" : assetId)}
            </h2>
            {list.map((d) => (
              <div key={d.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, padding: "8px 0", borderBottom: "1px solid #ddd", breakInside: "avoid" }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700 }}>
                    <span style={{ fontFamily: MONO }}>{d.ref}</span> · {d.title}
                    <span style={{ fontWeight: 600, color: "#555" }}> — {d.severity} · {d.status}{isOverdue(d) ? " · OVERDUE" : ""}</span>
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "#333" }}>{d.description || ""}</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#555", fontFamily: MONO }}>
                    {d.chainageStart !== "" && d.chainageStart !== undefined ? `CH ${fmtCh(d.chainageStart)}${d.chainageEnd ? `–${fmtCh(d.chainageEnd)}` : ""}` : ""}
                    {d.structureRef ? `  ${d.structureRef}` : ""}
                    {`  Raised ${fmtDate(d.raisedDate)} · Target ${d.targetDate ? fmtDate(d.targetDate) : "—"} · ${d.responsible || ""}`}
                  </p>
                </div>
                {(d.photos || []).length > 0 && (
                  <div style={{ display: "flex", gap: 4 }}>
                    {(d.photos || []).slice(0, 2).map((p, i) => (
                      <img key={i} src={p.url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        <style>{`
          @media print {
            @page { size: A4 portrait; margin: 12mm; }
            body * { visibility: hidden; }
            .def-print-sheet, .def-print-sheet * { visibility: visible; }
            .def-print-sheet { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; }
            .def-print-hide { display: none !important; }
          }
        `}</style>
      </div>
    </div>,
    document.body
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function DefectsSection({
  currentProject,
  currentProjectId,
  currentUser,
  progressAssets = [],
}) {
  const projectId = currentProject?.id || currentProjectId;

  const [defects, setDefects] = useState([]);
  const [modal, setModal] = useState(null);        // null | {} (new) | defect (edit)
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);  // defect whose photos are open
  const [printOpen, setPrintOpen] = useState(false);

  // Filters
  const [fStatus, setFStatus] = useState("OpenOnly"); // OpenOnly | All | a specific status
  const [fSeverity, setFSeverity] = useState("All");
  const [fAsset, setFAsset] = useState("All");

  // ── Subscription ──
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, "projects", projectId, "defects"), orderBy("ref"));
    const unsub = onSnapshot(q, (snap) => {
      setDefects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [projectId]);

  // ── Audit ──
  const logAudit = useCallback(async (action, entityId, summary) => {
    if (!projectId) return;
    try {
      await addDoc(collection(db, "projects", projectId, "auditLogs"), {
        entityType: "defect",
        entityId: entityId || "",
        action,
        summary: summary || "",
        userName: currentUser?.displayName || "",
        userEmail: currentUser?.email || "",
        createdAt: serverTimestamp(),
      });
    } catch (e) { /* best-effort */ }
  }, [projectId, currentUser]);

  // ── CRUD ──
  const saveDefect = useCallback(async (form) => {
    if (!projectId) return;
    setBusy(true);
    try {
      if (modal?.id) {
        const { id, ref, createdAt, ...rest } = { ...modal, ...form };
        await updateDoc(doc(db, "projects", projectId, "defects", modal.id), {
          ...rest, updatedAt: serverTimestamp(),
        });
      } else {
        const ref = nextDefRef(defects);
        const docRef = await addDoc(collection(db, "projects", projectId, "defects"), {
          ...form,
          ref,
          status: "Open",
          statusHistory: [{ status: "Open", date: todayISO(), by: currentUser?.email || "" }],
          createdAt: serverTimestamp(),
          createdBy: currentUser?.email || "",
        });
        await logAudit("create", docRef.id, `Defect ${ref} raised: ${form.title}`);
      }
      setModal(null);
    } finally {
      setBusy(false);
    }
  }, [projectId, modal, defects, currentUser, logAudit]);

  const deleteDefect = useCallback(async (d) => {
    if (!projectId) return;
    if (!window.confirm(`Delete ${d.ref} (${d.title})? This cannot be undone.`)) return;
    await deleteDoc(doc(db, "projects", projectId, "defects", d.id));
    await logAudit("delete", d.id, `Defect ${d.ref} deleted`);
  }, [projectId, logAudit]);

  // Advance close-out workflow one step; from Verified & Closed → Re-opened;
  // from Re-opened → Rectified (it has already been notified).
  const advanceStatus = useCallback(async (d) => {
    let next;
    if (d.status === "Verified & Closed") next = "Re-opened";
    else if (d.status === "Re-opened") next = "Rectified";
    else {
      const i = STATUS_FLOW.indexOf(d.status);
      next = STATUS_FLOW[Math.min(i + 1, STATUS_FLOW.length - 1)];
    }
    if (next === d.status) return;
    const patch = {
      status: next,
      statusHistory: [...(d.statusHistory || []), { status: next, date: todayISO(), by: currentUser?.email || "" }],
      updatedAt: serverTimestamp(),
    };
    if (next === "Verified & Closed") patch.closedDate = todayISO();
    await updateDoc(doc(db, "projects", projectId, "defects", d.id), patch);
    await logAudit("update", d.id, `Defect ${d.ref} → ${next}`);
  }, [projectId, currentUser, logAudit]);

  // ── Derived ──
  const filtered = useMemo(() => defects.filter((d) => {
    if (fStatus === "OpenOnly" && isClosed(d)) return false;
    if (fStatus !== "OpenOnly" && fStatus !== "All" && d.status !== fStatus) return false;
    if (fSeverity !== "All" && d.severity !== fSeverity) return false;
    if (fAsset !== "All" && (d.progressAssetId || "") !== fAsset) return false;
    return true;
  }), [defects, fStatus, fSeverity, fAsset]);

  const stats = useMemo(() => ({
    open: defects.filter((d) => !isClosed(d)).length,
    overdue: defects.filter(isOverdue).length,
    critical: defects.filter((d) => !isClosed(d) && d.severity === "Critical").length,
    closed: defects.filter(isClosed).length,
  }), [defects]);

  const assetName = useCallback(
    (id) => progressAssets.find((a) => a.id === id)?.name || "—",
    [progressAssets]
  );

  if (!projectId) {
    return (
      <section style={s.card}>
        <h2 style={s.title}>Defects & Snag List</h2>
        <p style={s.sub}>Select a project to manage defects.</p>
      </section>
    );
  }

  return (
    <div style={s.page}>
      <section style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={s.title}>Defects & Snag List</h2>
            <p style={s.sub}>
              Pre-completion snags and DLP defects, positioned by chainage against the route assets.
              Workflow: Open → Notified → Rectified → Verified & Closed.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={s.btn} onClick={() => setPrintOpen(true)}>🖨 Print Snag List</button>
            <button style={s.btnAccent} onClick={() => setModal({})}>+ Raise Defect</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
          <div style={s.statTile}><p style={s.statLabel}>Open</p><p style={{ ...s.statValue, color: stats.open ? C.danger : C.ink }}>{stats.open}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Overdue</p><p style={{ ...s.statValue, color: stats.overdue ? C.danger : C.ink }}>{stats.overdue}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Critical Open</p><p style={{ ...s.statValue, color: stats.critical ? C.danger : C.ink }}>{stats.critical}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Verified & Closed</p><p style={{ ...s.statValue, color: C.success }}>{stats.closed}</p></div>
        </div>

        {/* Defect map */}
        {progressAssets.length > 0 && (
          <div style={{ marginBottom: 18, padding: "14px 16px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, minWidth: 0 }}>
            <h3 style={s.h3}>Chainage Defect Map</h3>
            <DefectMap assets={progressAssets} defects={defects} onSelect={(d) => setModal(d)} />
          </div>
        )}

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <div>
            <label style={s.lbl}>Status</label>
            <select style={{ ...s.inp, width: "auto" }} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="OpenOnly">Open items only</option>
              <option value="All">All</option>
              {[...STATUS_FLOW, "Re-opened"].map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div>
            <label style={s.lbl}>Severity</label>
            <select style={{ ...s.inp, width: "auto" }} value={fSeverity} onChange={(e) => setFSeverity(e.target.value)}>
              <option>All</option>
              {SEVERITIES.map((sv) => <option key={sv}>{sv}</option>)}
            </select>
          </div>
          <div>
            <label style={s.lbl}>Asset</label>
            <select style={{ ...s.inp, width: "auto", maxWidth: 240 }} value={fAsset} onChange={(e) => setFAsset(e.target.value)}>
              <option value="All">All</option>
              {progressAssets.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          </div>
        </div>

        {/* Register */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Ref", "Title", "Location", "Category", "Severity", "Raised", "Target", "Status", "Photos", ""].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} style={{ ...s.td, textAlign: "center", color: C.inkMuted, padding: 24 }}>
                  No defects match the current filters.
                </td></tr>
              ) : filtered.map((d) => (
                <tr key={d.id} style={{ background: isOverdue(d) ? C.dangerSoft : "transparent" }}>
                  <td style={{ ...s.td, ...s.mono, fontWeight: 800, color: C.primary, whiteSpace: "nowrap" }}>{d.ref}</td>
                  <td style={{ ...s.td, minWidth: 200, cursor: "pointer" }} onClick={() => setModal(d)} title="Click to edit">
                    <span style={{ fontWeight: 600 }}>{d.title}</span>
                    {d.description && <span style={{ display: "block", fontSize: 11.5, color: C.inkMuted }}>{d.description.slice(0, 90)}{d.description.length > 90 ? "…" : ""}</span>}
                  </td>
                  <td style={{ ...s.td, ...s.mono, fontSize: 11.5, whiteSpace: "nowrap" }}>
                    {d.progressAssetId ? assetName(d.progressAssetId) : (d.structureRef || "—")}
                    {d.chainageStart !== "" && d.chainageStart !== undefined && (
                      <span style={{ display: "block", color: C.inkMuted }}>
                        CH {fmtCh(d.chainageStart)}{d.chainageEnd ? `–${fmtCh(d.chainageEnd)}` : ""}
                      </span>
                    )}
                  </td>
                  <td style={s.td}>{d.category}</td>
                  <td style={s.td}><span style={s.chip(SEVERITY_META[d.severity] || SEVERITY_META.Minor)}>{d.severity}</span></td>
                  <td style={{ ...s.td, ...s.mono, whiteSpace: "nowrap" }}>{fmtDate(d.raisedDate)}</td>
                  <td style={{ ...s.td, ...s.mono, whiteSpace: "nowrap", color: isOverdue(d) ? C.danger : C.ink, fontWeight: isOverdue(d) ? 700 : 400 }}>
                    {d.targetDate ? fmtDate(d.targetDate) : "—"}
                  </td>
                  <td style={s.td}>
                    <button
                      style={{ ...s.chip(STATUS_META[d.status] || STATUS_META.Open), cursor: "pointer" }}
                      title={isClosed(d) ? "Click to re-open" : "Click to advance"}
                      onClick={() => advanceStatus(d)}
                    >
                      {d.status} {isClosed(d) ? "↺" : "→"}
                    </button>
                  </td>
                  <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                    {(d.photos || []).length > 0 ? (
                      <button style={{ ...s.btn, padding: "5px 9px", fontSize: 11.5 }} onClick={() => setLightbox(d)}>
                        📷 {(d.photos || []).length}
                      </button>
                    ) : <span style={{ color: C.inkSubtle }}>—</span>}
                  </td>
                  <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                    <button style={s.btnDanger} onClick={() => deleteDefect(d)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {modal !== null && (
        <DefectModal
          initial={modal.id ? modal : null}
          assets={progressAssets}
          busy={busy}
          onSave={saveDefect}
          onClose={() => setModal(null)}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          photos={(lightbox.photos || []).map((p) => ({ url: p.url, caption: p.caption || lightbox.title }))}
          title={`${lightbox.ref} — ${lightbox.title}`}
          onClose={() => setLightbox(null)}
        />
      )}

      {printOpen && (
        <PrintSnagListModal
          defects={filtered}
          assets={progressAssets}
          currentProject={currentProject}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </div>
  );
}

export default React.memo(DefectsSection);
